import { useEffect, useRef, useState } from "react";
import { loadPdfDocument } from "../lib/pdfjs";

/**
 * Renders one page of a PDF onto a canvas, scaled to `width` CSS pixels
 * (sharpened for the device pixel ratio). Used both for certificate/CV
 * thumbnails (small width) and the expanded modal view (large width).
 */
export default function PdfPageCanvas({ url, pageNumber = 1, width = 320, className = "", onReady }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    loadPdfDocument(url)
      .then((pdf) => pdf.getPage(pageNumber))
      .then((page) => {
        if (cancelled) return undefined;
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const unscaled = page.getViewport({ scale: 1 });
        const scale = width / unscaled.width;
        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale });

        canvas.width = Math.ceil(viewport.width * dpr);
        canvas.height = Math.ceil(viewport.height * dpr);
        canvas.style.width = `${Math.ceil(viewport.width)}px`;
        canvas.style.height = `${Math.ceil(viewport.height)}px`;

        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        return page.render({ canvasContext: ctx, viewport }).promise.then(() => {
          if (!cancelled) {
            setStatus("ready");
            onReady && onReady(page);
          }
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("PDF render failed:", err);
          setStatus("error");
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, pageNumber, width]);

  return (
    <div className={`relative flex min-h-[60px] items-center justify-center ${className}`} data-status={status}>
      <canvas ref={canvasRef} className="block max-w-full bg-white" />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-alt text-[0.75rem] uppercase tracking-widest2 text-ink-faint">
          Rendering&hellip;
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-alt text-[0.75rem] uppercase tracking-widest2 text-red-700">
          Couldn&rsquo;t load PDF
        </div>
      )}
    </div>
  );
}
